import {
	CustomsDocumentInformation,
	CustomsInstructionsForHandling,
	IShipmentService,
	IShipmentsQueryParameters,
	Shipment,
	ShipmentDetails,
	ShipmentDirection,
	ShipmentParcelUnlockCodes,
} from '../@posturinn/client-react';
import { Guid, PagedResults } from '../@posturinn/common-react/core';
import {
	ADD_CUSTOMS_INFORMATION,
	ADD_CUSTOMS_INFORMATION_FAILED,
	ADD_CUSTOMS_INFORMATION_FULFILLED,
	DELETE_SHIPMENT,
	DELETE_SHIPMENT_FAILED,
	DELETE_SHIPMENT_FULFILLED,
	FETCH_LOCKER_CODE,
	FETCH_LOCKER_CODE_FAILED,
	FETCH_LOCKER_CODE_FULFILLED,
	FETCH_SHIPMENT,
	FETCH_SHIPMENT_DETAILS,
	FETCH_SHIPMENT_DETAILS_FAILED,
	FETCH_SHIPMENT_DETAILS_FULFILLED,
	FETCH_SHIPMENT_DOCUMENTS,
	FETCH_SHIPMENT_DOCUMENTS_FAILED,
	FETCH_SHIPMENT_DOCUMENTS_FULFILLED,
	FETCH_SHIPMENT_FAILED,
	FETCH_SHIPMENT_FEES,
	FETCH_SHIPMENT_FEES_FAILED,
	FETCH_SHIPMENT_FEES_FULFILLED,
	FETCH_SHIPMENT_FULFILLED,
	INBOUND_ACTIVE_SHIPMENTS_FAILED,
	INBOUND_ACTIVE_SHIPMENTS_FULFILLED,
	INBOUND_ACTIVE_SHIPMENTS_LOADING,
	INBOUND_DELIVERED_SHIPMENTS_FAILED,
	INBOUND_DELIVERED_SHIPMENTS_FULFILLED,
	INBOUND_DELIVERED_SHIPMENTS_LOADING,
	OUTBOUND_ACTIVE_SHIPMENTS_FAILED,
	OUTBOUND_ACTIVE_SHIPMENTS_FULFILLED,
	OUTBOUND_ACTIVE_SHIPMENTS_LOADING,
	OUTBOUND_DELIVERED_SHIPMENTS_FAILED,
	OUTBOUND_DELIVERED_SHIPMENTS_FULFILLED,
	OUTBOUND_DELIVERED_SHIPMENTS_LOADING,
	SET_SHIPMENT_VISIBILITY,
	SET_SHIPMENT_VISIBILITY_FAILED,
	SET_SHIPMENT_VISIBILITY_FULFILLED,
} from '../constants/actions/shipmentsActions';
import { closeAllModals } from './modalActions';
import { firstValueFrom } from 'rxjs';

export const shipmentsInboundLoading = (page: number) => ({
	type: INBOUND_ACTIVE_SHIPMENTS_LOADING,
	payload: {
		page,
	},
});

export const shipmentsInboundFulfilled = shipments => ({
	type: INBOUND_ACTIVE_SHIPMENTS_FULFILLED,
	payload: {
		...shipments,
	},
});

export const shipmentsInboundFailed = (page: number) => ({
	type: INBOUND_ACTIVE_SHIPMENTS_FAILED,
	payload: {
		page,
	},
});

export const shipmentsOutboundLoading = (page: number) => ({
	type: OUTBOUND_ACTIVE_SHIPMENTS_LOADING,
	payload: {
		page,
	},
});

export const shipmentsOutboundFulfilled = shipments => ({
	type: OUTBOUND_ACTIVE_SHIPMENTS_FULFILLED,
	payload: {
		...shipments,
	},
});

export const shipmentsOutboundFailed = (page: number) => ({
	type: OUTBOUND_ACTIVE_SHIPMENTS_FAILED,
	payload: {
		page,
	},
});

export const shipmentsOutboundDeliveredLoading = (page: number) => ({
	type: OUTBOUND_DELIVERED_SHIPMENTS_LOADING,
	payload: {
		page,
	},
});

export const shipmentsOutboundDeliveredFulfilled = shipments => ({
	type: OUTBOUND_DELIVERED_SHIPMENTS_FULFILLED,
	payload: {
		...shipments,
	},
});

export const shipmentsOutboundDeliveredFailed = (page: number) => ({
	type: OUTBOUND_DELIVERED_SHIPMENTS_FAILED,
	payload: {
		page,
	},
});

export const shipmentsInboundDeliveredLoading = (page: number) => ({
	type: INBOUND_DELIVERED_SHIPMENTS_LOADING,
	payload: {
		page,
	},
});

export const shipmentsInboundDeliveredFulfilled = shipments => ({
	type: INBOUND_DELIVERED_SHIPMENTS_FULFILLED,
	payload: { ...shipments },
});

export const shipmentsInboundDeliveredFailed = (page: number) => ({
	type: INBOUND_DELIVERED_SHIPMENTS_FAILED,
	payload: {
		page,
	},
});

export const loadingShipmentDocuments = shipmentId => ({
	type: FETCH_SHIPMENT_DOCUMENTS,
	payload: { shipmentId },
});

export const loadingShipmentDocumentsFulfilled = (documents, shipmentId) => ({
	type: FETCH_SHIPMENT_DOCUMENTS_FULFILLED,
	payload: {
		documents,
		shipmentId,
	},
});

export const loadingShipmentDocumentsFailed = shipmentId => ({
	type: FETCH_SHIPMENT_DOCUMENTS_FAILED,
	payload: { shipmentId },
});

export const loadingShipmentDetails = shipmentId => ({
	type: FETCH_SHIPMENT_DETAILS,
	payload: { shipmentId },
});

export const loadingShipmentDetailsFulfilled = (shipmentDetails: ShipmentDetails, shipmentId) => ({
	type: FETCH_SHIPMENT_DETAILS_FULFILLED,
	payload: {
		shipmentDetails,
		shipmentId,
	},
});

export const loadingShipmentDetailsFailed = shipmentId => ({
	type: FETCH_SHIPMENT_DETAILS_FAILED,
	payload: { shipmentId },
});

/**
 * Gets shipments from the API and dispatches the result
 *
 * @param {number} page - the page to fetch
 * @param {boolean} active - active=true gets shipments that are not delivered, false for delivered shipments
 * @param {ShipmentDirection} direction - Inbound or Outbound shipments
 * @param {boolean} includeHidden - Wether to include hidden shipments or not
 */
export const fetchShipments = (page: number, active: boolean, direction: ShipmentDirection, includeHidden: boolean=false) => {
	let fulfilledCallback = null;
	let	failedCallback = null;
	let	loadingCallback = null;
	if (active && direction === ShipmentDirection.Inbound) {
		fulfilledCallback = shipmentsInboundFulfilled;
		failedCallback = shipmentsInboundFailed;
		loadingCallback = shipmentsInboundLoading;
	} else if (!active && direction === ShipmentDirection.Inbound) {
		fulfilledCallback = shipmentsInboundDeliveredFulfilled;
		failedCallback = shipmentsInboundDeliveredFailed;
		loadingCallback = shipmentsInboundDeliveredLoading;
	} else if (active && direction === ShipmentDirection.Outbound) {
		fulfilledCallback = shipmentsOutboundFulfilled;
		failedCallback = shipmentsOutboundFailed;
		loadingCallback = shipmentsOutboundLoading;
	} else if (!active && direction === ShipmentDirection.Outbound) {
		fulfilledCallback = shipmentsOutboundDeliveredFulfilled;
		failedCallback = shipmentsOutboundDeliveredFailed;
		loadingCallback = shipmentsOutboundDeliveredLoading;
	} else {
		return null;
	}
	return async (dispatch, getState) => {
		const { injector } = getState().mobizReducer;
		const shipmentService: IShipmentService = injector.get(IShipmentService);
		const queryParams: IShipmentsQueryParameters = {
			direction: direction,
			page: page,
			pageSize: 5,
			active,
			sortBy: 'RecCreateDate',
			sortDirection: 'descending',
			includeHidden: includeHidden
		};

		dispatch(loadingCallback(page));
		try {
			shipmentService.getShipments(queryParams).subscribe({
				next: (shipments: PagedResults<Shipment>) => {
					dispatch(fulfilledCallback(shipments));
				},
				error: () => {
					dispatch(failedCallback(page));
				},
			});
		} catch (e) {
			dispatch(failedCallback(page));
		}
	};
};

export const fetchShipment = shipmentId => {
	return async (dispatch, getState) => {
		dispatch({ type: FETCH_SHIPMENT, payload: null });
		try {
			const { injector } = getState().mobizReducer;
			const shipmentService: IShipmentService = injector.get(IShipmentService);

			shipmentService.getShipment(shipmentId).subscribe({
				next: (shipment: Shipment) => {
					dispatch({ type: FETCH_SHIPMENT_FULFILLED, payload: shipment });
				},
				error: err => {
					dispatch({ type: FETCH_SHIPMENT_FAILED, payload: null });
					console.log(err);
				},
			});
		} catch (e) {
			dispatch({ type: FETCH_SHIPMENT_FAILED, payload: null });
		}
	};
};

export const fetchShipmentDocuments = shipmentId => {
	return fetchShipmentDetails(shipmentId);
};

// Attaches files if handling is IHaveReceipt
export const addShipmentCustomsInformation = (
	shipmentId,
	additionalInformation,
	files,
	handling,
	currentPage,
	email
) => {
	return async (dispatch, getState) => {
		dispatch({ type: ADD_CUSTOMS_INFORMATION });
		const { injector } = getState().mobizReducer;
		const shipmentService: IShipmentService = injector.get(IShipmentService);

		const customsDocumentInformation = new CustomsDocumentInformation();
		customsDocumentInformation.additionalInformation = additionalInformation;
		customsDocumentInformation.documents = handling === CustomsInstructionsForHandling.IHaveReceipt ? files : null;
		customsDocumentInformation.instructionsForHandling = handling;
		customsDocumentInformation.email = email;

		shipmentService.addCustomsInformation(shipmentId, customsDocumentInformation).subscribe({
			next: values => {
				dispatch({
					type: ADD_CUSTOMS_INFORMATION_FULFILLED,
					payload: { shipmentId, values, currentPage },
				});
				dispatch(closeAllModals());
				document.body.style.overflow = 'unset';
			},
			error: error => {
				let err = '';
				try {
					err = error.info.details;
				} catch (e) {
					//comment
				}
				dispatch({ type: ADD_CUSTOMS_INFORMATION_FAILED, payload: err });
			},
		});
	};
};

export const fetchShipmentDetails = shipmentId => {
	return async (dispatch, getState) => {
		dispatch(loadingShipmentDetails(shipmentId));
		try {
			const { injector } = getState().mobizReducer;
			const shipmentService: IShipmentService = injector.get(IShipmentService);

			shipmentService.getShipmentDetails(shipmentId).subscribe({
				next: shipmentDetails => {
					dispatch(loadingShipmentDetailsFulfilled(shipmentDetails, shipmentId));
				},
				error: () => {
					dispatch(loadingShipmentDetailsFailed(shipmentId));
				},
			});
		} catch (e) {
			dispatch(loadingShipmentDetailsFailed(shipmentId));
		}
	};
};

export const fetchShipmentFees = shipmentId => {
	return async (dispatch, getState) => {
		dispatch({ type: FETCH_SHIPMENT_FEES, payload: null });
		try {
			const { injector } = getState().mobizReducer;
			const shipmentService: IShipmentService = injector.get(IShipmentService);
			shipmentService.getShipmentFees(shipmentId, true).subscribe({
				next: fees => {
					dispatch({ type: FETCH_SHIPMENT_FEES_FULFILLED, payload: { fees, shipmentId } });
				},
				error: () => {
					dispatch({ type: FETCH_SHIPMENT_FEES_FAILED, payload: null });
				},
			});
		} catch (e) {
			dispatch({ type: FETCH_SHIPMENT_FEES_FAILED, payload: null });
		}
	};
};

export const changeDestination = (shipmentId, values) => {
	return async (dispatch, getState) => {
		dispatch({ type: FETCH_SHIPMENT, payload: null });
		try {
			const { injector } = getState().mobizReducer;
			const shipmentService: IShipmentService = injector.get(IShipmentService);

			const response = await shipmentService.changeShipmentDestination(shipmentId, values).toPromise();
			dispatch({ type: FETCH_SHIPMENT_FULFILLED, payload: response });
		} catch (e) {
			dispatch({ type: FETCH_SHIPMENT_FAILED, payload: e });
			throw e; // This ensures that the error is thrown and can be caught by try-catch in submitDestinationForm.
		}
	};
};

export const getShipmentParcelLockerCode = shipmentId => {
	return async (dispatch, getState) => {
		dispatch({ type: FETCH_LOCKER_CODE, payload: { shipmentId: shipmentId } });
		try {
			const { injector } = getState().mobizReducer;
			const shipmentService: IShipmentService = injector.get(IShipmentService);

			shipmentService.getShipmentParcelUnlockCodes(shipmentId).subscribe({
				next: (shipmentParcelLockerCode: ShipmentParcelUnlockCodes) => {
					dispatch({
						type: FETCH_LOCKER_CODE_FULFILLED,
						payload: { shipmentId: shipmentId, shipmentParcelLockerCode },
					});
				},
				error: () => {
					dispatch({ type: FETCH_LOCKER_CODE_FAILED, payload: { shipmentId: shipmentId } });
				},
			});
		} catch (e) {
			dispatch({ type: FETCH_LOCKER_CODE_FAILED, payload: { shipmentId: shipmentId } });
		}
	};
};

export const deleteShipment = shipmentId => {
	return async (dispatch, getState) => {
		dispatch({ type: DELETE_SHIPMENT, payload: null });
		try {
			const { injector } = getState().mobizReducer;
			const shipmentService: IShipmentService = injector.get(IShipmentService);

			const response = await shipmentService.deleteShipment(shipmentId).toPromise();
			dispatch({ type: DELETE_SHIPMENT_FULFILLED, payload: response });
		} catch (e) {
			dispatch({ type: DELETE_SHIPMENT_FAILED, payload: e });
			throw e;
		}
	};
};

/**
 * Hides or unhides a shipment
 * @param {Guid} shipmentId - Shipment to change visibility
 * @param {boolean} hide - Hide or show shipment
 */
export const setShipmentVisibility = (shipmentId: Guid, hide: boolean) => {
	return async (dispatch, getState) => {
		dispatch({ type: SET_SHIPMENT_VISIBILITY, payload: { hide: hide } });
		try {
			const { injector } = getState().mobizReducer;
			const shipmentService: IShipmentService = injector.get(IShipmentService);
			
			const response = await firstValueFrom(shipmentService.setShipmentVisibility(shipmentId, hide));
			dispatch({ type: SET_SHIPMENT_VISIBILITY_FULFILLED, payload: response });
		} catch (e) {
			dispatch({ type: SET_SHIPMENT_VISIBILITY_FAILED, payload: e });
			throw e;
		}
	};
};
